import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, FormLabel, Group, IconExternalLink, IconRefresh, Input, Typography, Wrapper } from '@screentone/core';

import { useConvertedProperty } from 'hooks';
import { getAllessehAdminUrl, getAllessehSummariesUrl, parseUrlForAllessehId } from '../Debug.utils';

interface DebugPageFormProps {
  initialValue?: string;
  hasError?: boolean;
  onSubmit?: () => void;
  isRefreshing?: boolean;
  isLoading?: boolean;
  onRefresh?: () => void;
  resolvedAllessehId?: string;
}

const DebugPageForm = ({
  initialValue = '',
  hasError = false,
  onSubmit,
  isRefreshing = false,
  isLoading = false,
  onRefresh,
  resolvedAllessehId = ''
}: DebugPageFormProps) => {
  const navigate = useNavigate();
  const urlInputRef = useRef<HTMLInputElement>(null);
  const currentProperty = useConvertedProperty();

  const [inputValue, setInputValue] = useState(initialValue);

  // Sync input value with prop changes
  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  const handleSubmit = () => {
    if (urlInputRef.current) {
      const url = urlInputRef.current.value;
      const parsedId = parseUrlForAllessehId(url);
      if (parsedId) {
        // Navigate to the dynamic route with the allessehId
        navigate(`/${currentProperty}/debug/${parsedId}`);
        // Call optional callback
        onSubmit?.();
      }
    }
  };

  return (
    <>
      {(!resolvedAllessehId || hasError) && (
        <Group fullWidth direction="column" gap="sm">
          <FormLabel fullWidth label="Page URL or Allesseh ID" labelPosition="top">
            <Input
              placeholder="Enter tool URL or Allesseh ID"
              componentRef={urlInputRef}
              value={inputValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
            />
          </FormLabel>
          <Button primary onClick={handleSubmit}>
            Submit
          </Button>
          {hasError && (
            <Typography color="lava">{`No data found for "${inputValue}". Valid values are Allesseh ID (UCSD_WSJ_), or the tool url, e.g. http://curation.dowjones.io/wsj/homepage`}</Typography>
          )}
        </Group>
      )}

      {resolvedAllessehId && !isLoading && !hasError && (
        <Wrapper margin={{ top: 'md', bottom: 'md' }}>
          <Group margin={{ bottom: 'sm' }} direction="row" align="space-between">
            <Button
              tertiary
              onClick={onRefresh}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              icon={IconRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Group direction="row">
              {getAllessehAdminUrl(resolvedAllessehId, 'collection') && (
                <Button
                  tertiary
                  icon={IconExternalLink}
                  iconPosition="right"
                  onClick={() => {
                    const url = getAllessehAdminUrl(resolvedAllessehId, 'collection');
                    if (url) window.open(url, '_blank');
                  }}
                >
                  Allesseh Admin
                </Button>
              )}
              {getAllessehSummariesUrl(resolvedAllessehId) && (
                <Button
                  tertiary
                  componentEl="a"
                  href={getAllessehSummariesUrl(resolvedAllessehId) ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={IconExternalLink}
                  iconPosition="right"
                >
                  Summaries
                </Button>
              )}
            </Group>
          </Group>
        </Wrapper>
      )}
    </>
  );
};

export default DebugPageForm;
